import { getProjectErrorForCode, getProjectErrorFromRawError, isProjectError } from '../../utils/errors';
import { ProjectErrors, ProjectErrorType } from '../../utils/errors/types';
import { Profile, ProfileTransactionsAndPurchases, Purchase, Transaction } from '../types/profile';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, deleteDoc } from 'firebase/firestore';
import { UpdateProfileDto } from '../typeDtos/profile';
import { Deal } from '../types/others';
import { executeCloudFunction } from '../index';
import { deleteField } from 'firebase/firestore';
import { parseContent } from '../utils';
import { parseProfile } from './parser';

export const getProfileByUserId = async (
  id: string,
  adminRequired: boolean,
): Promise<ProjectErrorType | Profile | undefined> => {
  try {
    const _query = await getDoc(doc(db, 'profiles', id));
    if (!_query.exists()) {
      console.log('No such document!');
      return undefined;
    }
    const result = await parseProfile(parseContent(_query));
    const resultError = isProjectError(result);
    if (resultError) {
      return result as ProjectErrorType;
    }

    const _result = result as Profile;
    if (adminRequired && !_result?.isAdmin) {
      return getProjectErrorForCode(ProjectErrors.userIsNotAdmin);
    }

    return _result;
  } catch (error) {
    console.log('[getProfileByUserId] Error:', error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const getProfileTransactionsAndPurchases = async (
  id: string,
): Promise<ProjectErrorType | ProfileTransactionsAndPurchases> => {
  try {
    const transactionsRef = collection(db, 'profiles', id, 'transactions');
    const transactionsSnap = await getDocs(transactionsRef);
    const transactions = transactionsSnap.docs.map((d) => ({ id: d.id, ...d.data() } as Transaction));
    const purchasesRef = collection(db, 'profiles', id, 'purchases');
    const purchasesSnap = await getDocs(purchasesRef);
    const purchases = purchasesSnap.docs.map((d) => ({ id: d.id, ...d.data() } as Purchase));
    return { transactions, purchases };
  } catch (error) {
    console.log('[getProfileTransactionsAndPurchases] Error:', error);
    return getProjectErrorFromRawError(error as Error);
  }
};

export const updateProfile = async (data: UpdateProfileDto): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const nowTs = new Date().getTime();
    const _data = { ...data, updatedAt: nowTs };
    const docRef = doc(db, 'profiles', _data?.id);
    await updateDoc(docRef, _data);
    return { success: true };
  } catch (e) {
    console.log('[PROFILES] Error at updateProfile:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updatePromoCodeData = async ({ profileId, gifts }: { profileId: string; gifts?: Deal[] }) => {
  try {
    await executeCloudFunction('updatePromoCodeData', { profileId, gifts });
  } catch (e) {
    console.log('[PROFILES] Error at updatePromoCodeData:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteProfileSubCollectionDocs = async ({
  profileId,
  subCollectionName,
}: {
  profileId: string;
  subCollectionName: string;
}) => {
  try {
    const _query = query(collection(db, 'profiles', profileId, subCollectionName));
    const _docs = await getDocs(_query);
    let deletedDocsCount = 0;
    _docs.forEach((_doc) => {
      deleteDoc(_doc.ref);
      deletedDocsCount++;
    });
    return { success: true, count: deletedDocsCount };
  } catch (e) {
    return getProjectErrorFromRawError(e as Error);
  }
};

export const copyUserData = async (oldUserId: string, newUserId: string) => {
  try {
    return await executeCloudFunction('copyUserData', { oldUserId, newUserId });
  } catch (e) {
    console.log('[PROFILES] Error at copyUserData:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteProfileSurveyAndLearningPathFields = async (profileId: string) => {
  try {
    const docRef = doc(db, 'profiles', profileId);
    await updateDoc(docRef, {
      initialSurvey: deleteField(),
      currentSurveys: deleteField(),
      learningPaths: deleteField(),
      currentLearningPaths: deleteField(),
    });
    return { success: true };
  } catch (e) {
    return getProjectErrorFromRawError(e as Error);
  }
};
